/**
 * File: notifications.ts
 * Path: packages/config/src/notifications/notifications.ts
 * Description: The admin's push notifications, shared by the admin (Settings >
 * Notifications) and the server (`notify`, apps/server/src/shared/push).
 *
 * Each event names the permission a person needs to receive it, so nobody is
 * told about something their role can't open. Adding an event here is all
 * another feature needs to do for it to appear in everyone's settings; the
 * server then sends it with `notify({ event, ... })`.
 *
 * Liam's choices (2026-10-08): no quiet hours (everything arrives straight
 * away), full names and details on the lock screen, and push instead of the
 * admin emails with no email fallback. So every notification is also kept in
 * the bell, and each device confirms what it receives (see NotificationItem).
 */

import type { Permission } from "@idk/types";

export interface NotificationEventInfo {
  label: string;
  /** One line under the switch in Settings. */
  description: string;
  /** Who may receive it. `null`: any active staff member. */
  permission: Permission | null;
  /** Whether it is on for someone who never touched the switch. */
  defaultOn: boolean;
  /** Not listed in Settings (sent on request, never filtered). */
  hidden?: boolean;
}

export const NOTIFICATION_EVENTS = {
  enquiry: {
    label: "New enquiry",
    description: "Someone sends the contact form on the website.",
    permission: "sales",
    defaultOn: true,
  },
  websiteQuote: {
    label: "Website quote",
    description: "Someone gets a quote on the website (saved on their lead).",
    permission: "sales",
    defaultOn: true,
  },
  websiteAudit: {
    label: "Website audit",
    description: "Someone has the free website audit emailed to them.",
    permission: "sales",
    defaultOn: true,
  },
  websiteBrandKit: {
    label: "Brand kit",
    description: "Someone has the free brand kit emailed to them.",
    permission: "sales",
    defaultOn: true,
  },
  contractSigned: {
    label: "Client signed a contract",
    description: "A client signs: it needs your countersignature.",
    permission: "sales",
    defaultOn: true,
  },
  contractOpened: {
    label: "Client is reading a contract",
    description: "A client confirmed their email code and opened the contract.",
    permission: "sales",
    defaultOn: false,
  },
  callsToday: {
    label: "Follow-up reminders",
    description: "07:00: only what is due that day: lead follow-ups, payments, unsigned contracts and quotes ending.",
    permission: "sales",
    defaultOn: true,
  },
  referral: {
    label: "New referral",
    description: "A referral partner's code is used on a quote.",
    permission: "referrals",
    defaultOn: true,
  },
  taxDeadline: {
    label: "Tax and filing deadlines",
    description: "A week before, the day before and on the day: provisional tax, returns, staff and VAT.",
    permission: "finance",
    defaultOn: true,
  },
  budgetOverspent: {
    label: "Over budget",
    description: "A cost account passes its budget for the month: once per account per month.",
    permission: "finance",
    defaultOn: true,
  },
  paymentDue: {
    label: "Bills and renewals",
    description: "Bills and renewals: on the 1st of the month to pay ahead, and 3 days before if still unpaid.",
    permission: "finance",
    defaultOn: true,
  },
  deliveryFailed: {
    label: "Document email failed",
    description: "A quote, audit or brand kit email to a customer failed.",
    permission: "logs",
    defaultOn: false,
  },
  test: {
    label: "Test",
    description: "Sent when you press Test all my devices.",
    permission: null,
    defaultOn: true,
    hidden: true,
  },
} as const satisfies Record<string, NotificationEventInfo>;

export type NotificationEvent = keyof typeof NOTIFICATION_EVENTS;

export const NOTIFICATION_EVENT_IDS = Object.keys(NOTIFICATION_EVENTS) as NotificationEvent[];

export const isNotificationEvent = (value: unknown): value is NotificationEvent =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(NOTIFICATION_EVENTS, value);

/** `notificationSettings/{uid}`: each person's own choices. */
export interface NotificationSettings {
  /** True once a device has been switched on; the server only notifies these people. */
  enabled: boolean;
  /** Per event; missing means the event's `defaultOn`. */
  events: Partial<Record<NotificationEvent, boolean>>;
}

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
  enabled: false,
  events: {},
};

/** A device that hasn't confirmed anything for longer than this is flagged in Settings. */
export const DEVICE_CONFIRMED_WITHIN_MS = 7 * 24 * 60 * 60 * 1000;
/** How many notifications the bell keeps showing. */
export const NOTIFICATION_INBOX_LIMIT = 30;

/** Reads a stored settings document, filling anything missing or malformed with the defaults. */
export const toNotificationSettings = (data: unknown): NotificationSettings => {
  const raw = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const events: NotificationSettings["events"] = {};
  if (raw.events && typeof raw.events === "object") {
    for (const [key, value] of Object.entries(raw.events as Record<string, unknown>)) {
      if (isNotificationEvent(key) && typeof value === "boolean") events[key] = value;
    }
  }
  return {
    enabled: raw.enabled === true,
    events,
  };
};

export const wantsEvent = (settings: NotificationSettings, event: NotificationEvent): boolean =>
  settings.events[event] ?? NOTIFICATION_EVENTS[event].defaultOn;

/** Where in the admin each notification opens. Paths only; the service worker adds the origin. */
export const isAdminPath = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//") && value.length <= 300;

/**
 * Where it all lives. Each person reads and writes only their own (firestore.rules):
 * - `notificationSettings/{uid}`: NotificationSettings
 * - `pushDevices/{uid}/devices/{deviceId}`: one per browser or phone (server-written)
 * - `notifications/{uid}/inbox/{id}`: every notification sent, for the bell (server-written)
 */
export const NOTIFICATION_COLLECTIONS = {
  settings: "notificationSettings",
  devices: "pushDevices",
  deviceList: "devices",
  inbox: "notifications",
  inboxItems: "inbox",
} as const;

/** Where a notification is in its life. */
export type NotificationStatus =
  /** Being sent right now. */
  | "sending"
  /** Reached Google for at least one device; waiting for a device to confirm. */
  | "sent"
  /** A device showed it and said so. */
  | "confirmed"
  /** It reached no device (none switched on, or every send failed). Still in the bell. */
  | "unsent";

/** One notification in a person's bell. Times are epoch milliseconds. */
export interface NotificationItem {
  id: string;
  event: NotificationEvent;
  title: string;
  body: string;
  url: string;
  status: NotificationStatus;
  createdAt: number;
  devicesSent: number;
  confirmedAt: number | null;
  /** Label of the device that confirmed first ("iPhone, Safari"). */
  confirmedBy: string | null;
  readAt: number | null;
}

/** One device that receives notifications. */
export interface PushDevice {
  id: string;
  label: string;
  /** Opened from the home screen / installed, rather than in a browser tab. */
  installed: boolean;
  createdAt: number;
  lastSeenAt: number;
  lastSentAt: number | null;
  lastConfirmedAt: number | null;
}

const num = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
const str = (value: unknown, fallback = ""): string => (typeof value === "string" ? value : fallback);

const STATUSES: readonly NotificationStatus[] = ["sending", "sent", "confirmed", "unsent"];

export const toNotificationItem = (id: string, data: Record<string, unknown>): NotificationItem => ({
  id,
  event: isNotificationEvent(data.event) ? data.event : "test",
  title: str(data.title),
  body: str(data.body),
  url: isAdminPath(data.url) ? data.url : "/dashboard",
  status: STATUSES.includes(data.status as NotificationStatus) ? (data.status as NotificationStatus) : "sent",
  createdAt: num(data.createdAt) ?? 0,
  devicesSent: num(data.devicesSent) ?? 0,
  confirmedAt: num(data.confirmedAt),
  confirmedBy: typeof data.confirmedBy === "string" ? data.confirmedBy : null,
  readAt: num(data.readAt),
});

export const toPushDevice = (id: string, data: Record<string, unknown>): PushDevice => ({
  id,
  label: str(data.label, "Unknown device"),
  installed: data.installed === true,
  createdAt: num(data.createdAt) ?? 0,
  lastSeenAt: num(data.lastSeenAt) ?? 0,
  lastSentAt: num(data.lastSentAt),
  lastConfirmedAt: num(data.lastConfirmedAt),
});

/** A device that confirmed a notification within DEVICE_CONFIRMED_WITHIN_MS. */
export const deviceIsWorking = (device: Pick<PushDevice, "lastConfirmedAt">, now: number): boolean =>
  device.lastConfirmedAt !== null && now - device.lastConfirmedAt <= DEVICE_CONFIRMED_WITHIN_MS;
